import { useForm } from "react-hook-form";
import { Button, Modal, Textarea } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { AdminOrderDetailDTO } from "@wmart/shared-types";

interface CancelFormValues {
  reason: string;
}

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const REASON_MAX = 500;

/**
 * Cancel an order. POST /v1/admin/orders/:id/cancel — `reason` is required
 * (1-500 chars) per `CancelOrderBody`. 409 `order.invalid_state_for_cancel`
 * surfaces via the toast provider when the order is not in `paid`.
 *
 * Rendered inside `<Modal>` (Plan 11 T11) — ESC + click-outside +
 * focus-trap + scroll-lock all come from the primitive.
 */
export function CancelOrderModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<CancelFormValues>({
    defaultValues: { reason: "" },
  });

  const reason = watch("reason");

  async function onSubmit(values: CancelFormValues) {
    try {
      await api.post(`/v1/admin/orders/${order.id}/cancel`, {
        reason: values.reason,
      });
      notify("success", "Order cancelled");
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Cancel failed");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Cancel order"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Back
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || reason.trim() === ""}
            loading={isSubmitting}
          >
            Cancel order
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <Textarea
          label="Reason"
          rows={4}
          maxLength={REASON_MAX}
          helper={`${reason.length}/${REASON_MAX}`}
          error={errors.reason?.message}
          {...register("reason", {
            required: "Reason is required",
            minLength: { value: 1, message: "Reason cannot be empty" },
            maxLength: {
              value: REASON_MAX,
              message: `Max ${REASON_MAX} characters`,
            },
          })}
        />
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
